// The one way the app opens a server-rendered print (a PDF): the tax invoice, packing list, delivery
// slips (D153.5) and the credit note (D157.16). No server print is fetched or opened anywhere else,
// except the purchase-order preview (D165): its own POST (previewVendorPurchaseOrders), opened here.
import { API_BASE_URL, handleResponse } from './apiClient';
import { getAuthHeader } from './authSession';

/**
 * A server-rendered print as a blob, from `path` under /api. The JWT is only ever sent as a header
 * (no cookie), so a bare window.open of the URL is always 401: fetch with the header, and let a
 * refusal throw through apiClient's one error path (status/code intact).
 */
export async function fetchServerPrint(path: string): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: getAuthHeader() });
  if (!res.ok) await handleResponse(res);
  return res.blob();
}

/**
 * Shows a print in a new tab. The tab opens synchronously: after the await it would no longer be
 * inside the click gesture and popup blockers would drop it.
 */
export async function openPrintInNewTab(
  fetchPrint: () => Promise<Blob>,
  { onBlocked, onError }: { onBlocked: () => void; onError: (e: unknown) => void },
): Promise<void> {
  const win = window.open('', '_blank');
  if (!win) {
    onBlocked();
    return;
  }
  try {
    const url = URL.createObjectURL(await fetchPrint());
    win.location.href = url;
    // the new tab has loaded the blob well within a minute
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    win.close();
    onError(e);
  }
}
